/**
 * Expense Form Component
 * 
 * Form for adding new expenses to a group.
 * Following Tabby design principles: minimal, functional, clear.
 */

import React, { useState } from 'react';
import { Participant, SplitMode } from '../features/expenseSplitter/groupModel';
import { CreateExpenseParams, validateExpenseParams } from '../features/expenseSplitter/expenseService';
import { parseCurrencyInput, getCurrencyPlaceholder, validateCurrencyAmount } from '../utils/currency';
import CurrencyPicker from './CurrencyPicker';

interface ExpenseFormProps {
  participants: Participant[];
  defaultCurrency: string;
  onSubmit: (params: CreateExpenseParams) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  participants,
  defaultCurrency,
  onSubmit,
  onCancel,
  isSubmitting = false
}) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [paidBy, setPaidBy] = useState(participants[0]?.id || '');
  const [splitMode, setSplitMode] = useState<SplitMode>(SplitMode.EQUAL);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    participants.map(p => p.id)
  );
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Validate currency amount first
      const amountValidation = validateCurrencyAmount(amount, currency);
      if (!amountValidation.valid) {
        setError(amountValidation.error || 'Invalid amount');
        return;
      }

      const amountInCents = parseCurrencyInput(amount, currency);
      
      const params: CreateExpenseParams = {
        description: description.trim(),
        amount: amountInCents,
        currency: currency,
        paidBy,
        splitMode,
        participantIds: selectedParticipants,
        notes: notes.trim() || undefined
      };

      // Validate before submitting
      validateExpenseParams(params);

      await onSubmit(params);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add expense');
    }
  };

  const handleParticipantToggle = (participantId: string) => {
    setSelectedParticipants(prev => {
      if (prev.includes(participantId)) {
        // Don't allow deselecting all participants
        if (prev.length <= 1) {
          return prev;
        }
        return prev.filter(id => id !== participantId);
      } else {
        return [...prev, participantId];
      }
    });
  };

  const isFormValid = () => {
    return (
      description.trim() &&
      amount &&
      parseFloat(amount) > 0 &&
      paidBy &&
      selectedParticipants.length > 0
    );
  };

  return (
    <div className="expense-form" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 'var(--radius-lg)',
        padding: '32px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <h2 style={{ marginBottom: '24px' }}>Add Expense</h2>

        {error && (
          <div style={{
            backgroundColor: 'var(--color-error)',
            color: 'white',
            padding: '12px',
            borderRadius: 'var(--radius-base)',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'var(--font-weight-medium)' 
            }}>
              Description *
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What was this expense for?"
              maxLength={200}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-base)',
                fontSize: '14px'
              }}
              required
            />
          </div>

          {/* Amount and Currency */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'var(--font-weight-medium)' 
            }}>
              Amount *
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={getCurrencyPlaceholder(currency)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-base)',
                  fontSize: '14px'
                }}
                required
              />
              <div style={{ width: '120px' }}>
                <CurrencyPicker
                  value={currency}
                  onChange={setCurrency}
                  size="medium"
                  showSymbol={false}
                />
              </div>
            </div>
          </div>

          {/* Paid By */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'var(--font-weight-medium)' 
            }}>
              Paid by *
            </label>
            <select
              value={paidBy}
              onChange={(e) => setPaidBy(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-base)',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
              required
            >
              {participants.map(participant => (
                <option key={participant.id} value={participant.id}>
                  {participant.name}
                </option>
              ))}
            </select>
          </div>

          {/* Split Between */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontSize: '14px', 
              fontWeight: 'var(--font-weight-medium)' 
            }}>
              Split between *
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {participants.map(participant => (
                <label key={participant.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-base)',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedParticipants.includes(participant.id)}
                    onChange={() => handleParticipantToggle(participant.id)}
                    style={{ marginRight: '8px' }}
                  />
                  <span>{participant.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Split Mode */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'var(--font-weight-medium)' 
            }}>
              Split method
            </label>
            <select
              value={splitMode}
              onChange={(e) => setSplitMode(e.target.value as SplitMode)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-base)',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value={SplitMode.EQUAL}>Split equally</option>
              <option value={SplitMode.PERCENTAGE}>By percentage</option>
              <option value={SplitMode.SHARES}>By shares</option>
              <option value={SplitMode.CUSTOM}>Custom amounts</option>
            </select>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'var(--font-weight-medium)' 
            }}>
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details..."
              rows={3}
              maxLength={500}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-base)',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid() || isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? 'Adding...' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;
